import { create } from 'zustand';
import type { NFTData } from '../types';
import { fetchNFTByNumber } from '../services/solana';

interface NFTState {
  currentNFT: NFTData | null;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  fetchedNFTs: Map<number, NFTData>;

  fetchNFTByNum: (nftNumber: number) => Promise<void>;
  clearCurrentNFT: () => void;
  setSearchQuery: (query: string) => void;
  clearError: () => void;
}

export const useNFTStore = create<NFTState>((set, get) => ({
  currentNFT: null,
  isLoading: false,
  error: null,
  searchQuery: '',
  fetchedNFTs: new Map(),

  fetchNFTByNum: async (nftNumber: number) => {
    if (!nftNumber || nftNumber <= 0) {
      set({ error: 'Please enter a valid NFT number' });
      return;
    }

    const cached = get().fetchedNFTs.get(nftNumber);
    if (cached) {
      set({ currentNFT: cached, error: null });
      return;
    }

    set({ isLoading: true, error: null });

    const result = await fetchNFTByNumber(nftNumber);

    if (result.success && result.data) {
      const newCache = new Map(get().fetchedNFTs);
      newCache.set(nftNumber, result.data);

      set({
        currentNFT: result.data,
        isLoading: false,
        error: null,
        fetchedNFTs: newCache,
      });
    } else {
      set({
        currentNFT: null,
        isLoading: false,
        error: result.error || 'Failed to fetch NFT',
      });
    }
  },

  clearCurrentNFT: () => {
    set({ currentNFT: null, error: null, searchQuery: '' });
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
  },

  clearError: () => {
    set({ error: null });
  },
}));
